/* cSpell:ignore noneditable checkmarks ampm */
import React from "react";

import { faCircleCheck, faCircleXmark, faFlagPennant } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	Checkbox,
	FormDatePicker,
	FormField,
	FormInputNumeric,
	FormSubscribe,
	Icon,
	SortableOrderedList,
	Text,
	TextDate,
	cn,
	usePyForm,
} from "@performyard/py-components";
import { DEFAULT_TIMEZONE, convertToLocalZonedTime, convertToZonedUTCTime } from "@performyard/utils-isomorphic";

import { DEFAULT_DUE_TIME } from "../utils/constants";
import { formatDateViewOnly } from "../utils/dateFormatUtils";
import { STATIC_EMPLOYEE_OPTIONS, renderEmployeeOrderedItemReadOnly } from "../utils/employeeSelectionUtils";
import { computeRelativeDueDate } from "../utils/relativeDueDate";

import { type EmployeeValue, MultipleOrderedSelectEmployee } from "../../Inputs/SelectEmployee";
import type { ReviewFormContainerProps } from "../ReviewFormContainer";
import { ReviewFormContainer } from "../ReviewFormContainer";

export type StandardReviewFormCardEditableField =
	| "formAuthors"
	| "formSigners"
	| "authorsDueDate"
	| "signersDueDate"
	| "makeVisibleWhenCompleted"
	| "redactAuthors";

export type StandardReviewFormCardProps = {
	/**
	 * Whether to show due dates as relative (days) or static (actual dates)
	 * - When true: Authors shows "days from start date", Signers shows "days from authors due date"
	 * - When false: Both show date pickers
	 * @default false
	 */
	isRelativeDates?: boolean;
	/**
	 * Minimum allowed due date (e.g., review cycle start date)
	 * Only applies to fixed/static dates, not relative dates
	 * When set, date pickers will disable dates before this date
	 */
	minimumDueDate?: Date;
	/**
	 * The start date used to calculate relative dates
	 * Only used when isRelativeDates is true
	 * Displays in a banner to show the reference date for relative calculations
	 */
	relativeDateStartDate?: Date;
	/**
	 * Company timezone used to compute the relative due date/time shown in the banner.
	 * Only used when isRelativeDates is true.
	 * @default DEFAULT_TIMEZONE
	 */
	companyTimezone?: string;
	/**
	 * Company default review-cycle due time (hour of day, 0-23) that the due dates will default to.
	 * @default DEFAULT_DUE_TIME
	 */
	defaultDueTime?: number;
	/**
	 * Whether all fields are view-only
	 * @default false
	 */
	readOnly?: boolean;
	/**
	 * Specific fields that should be editable when readOnly is true
	 * Only applies when readOnly is true - these fields will be editable while others remain view-only
	 * @example ["authorsDueDate", "signersDueDate"]
	 */
	editableFields?: Array<StandardReviewFormCardEditableField>;
	/**
	 * Optional CSS classes to apply to the container
	 */
	dangerouslySetClassName?: string;
};

/**
 * StandardReviewFormCard displays the form fields for a standard review form.
 * This should be wrapped in a PYForm component with the standardFormSchema.
 */
export const StandardReviewFormCard = ({
	isRelativeDates = false,
	minimumDueDate,
	relativeDateStartDate,
	companyTimezone = DEFAULT_TIMEZONE,
	defaultDueTime = DEFAULT_DUE_TIME,
	readOnly = false,
	editableFields = [],
	dangerouslySetClassName,
}: StandardReviewFormCardProps) => {
	const { id: formId } = usePyForm();

	// Helper to check if a field is editable
	const isFieldEditable = (fieldName: string) => {
		if (!readOnly) return true; // All fields editable when not in readOnly mode
		return editableFields.includes(fieldName as any); // Field is editable if in editableFields list
	};

	// Banner preview due date: relative days added to the start date, snapped to the company
	// default due time in the company timezone (mirrors the backend generate-form-assignments).
	// The wizard hands us a "localized" start (local getters = company wall-clock), so convert to a
	// real UTC instant for the shared calc, then back to localized for TextDate (which formats in
	// local time). Nested signer calls round-trip losslessly through the same conversion.
	const calculateRelativeDueDateInCompanyTz = (startDate: Date, daysToAdd: number) => {
		// Generate the UTC Date of the start date according to the company TZ
		const startDateInCompanyTzAsUtc = convertToZonedUTCTime(startDate, companyTimezone);
		// Calculate the due date using the company timezone (DST changes, etc)
		const dueDateInCompanyTzAsUtc = computeRelativeDueDate({
			startDate: startDateInCompanyTzAsUtc,
			daysToAdd,
			defaultDueTime,
			companyTimezone,
		});

		const dueDateInCompanyTzAsLocalTime = convertToLocalZonedTime(dueDateInCompanyTzAsUtc, companyTimezone);

		return dueDateInCompanyTzAsLocalTime;
	};

	// Readonly view with no editable fields
	if (readOnly && editableFields.length === 0) {
		return (
			<FormSubscribe
				id="standard-form-card-readonly"
				selector={(state) => ({
					formAuthors: state.values.formAuthors || [],
					formSigners: state.values.formSigners || [],
					authorsDueDate: state.values.authorsDueDate,
					signersDueDate: state.values.signersDueDate,
					makeVisibleWhenCompleted: state.values.makeVisibleWhenCompleted,
					redactAuthors: state.values.redactAuthors,
				})}
			>
				{({
					formAuthors,
					formSigners,
					authorsDueDate,
					signersDueDate,
					makeVisibleWhenCompleted,
					redactAuthors,
				}) => (
					<div className={cn("flex flex-col gap-4", dangerouslySetClassName)}>
						{formAuthors.length > 0 && (
							<div key="form-authors-readonly" className="grid grid-cols-[180px_1fr] gap-2">
								<Text
									size="paragraph-3"
									weight="semi-bold"
									color="neutral-9"
									dangerouslySetClassName="flex items-start pt-1"
								>
									Form Authors
								</Text>
								<SortableOrderedList<EmployeeValue>
									values={formAuthors}
									readOnly
									renderItem={renderEmployeeOrderedItemReadOnly}
									getItemId={(employee) => employee.id}
								/>
							</div>
						)}

						{formSigners.length > 0 && (
							<div key="form-signers-readonly" className="grid grid-cols-[180px_1fr] gap-2">
								<Text
									size="paragraph-3"
									weight="semi-bold"
									color="neutral-9"
									dangerouslySetClassName="flex items-start pt-1"
								>
									Form Signers
								</Text>
								<SortableOrderedList<EmployeeValue>
									values={formSigners}
									readOnly
									renderItem={renderEmployeeOrderedItemReadOnly}
									getItemId={(employee) => employee.id}
								/>
							</div>
						)}

						{formAuthors.length > 0 && authorsDueDate != null && (
							<div
								key="authors-due-date-readonly"
								className="grid min-h-8 grid-cols-[180px_1fr] items-center gap-2"
							>
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									Authors Due Date
								</Text>
								{formatDateViewOnly(authorsDueDate, "authors")}
							</div>
						)}

						{formSigners.length > 0 && signersDueDate != null && (
							<div
								key="signers-due-date-readonly"
								className="grid min-h-8 grid-cols-[180px_1fr] items-center gap-2"
							>
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									Signers Due Date
								</Text>
								{formatDateViewOnly(signersDueDate, "signers")}
							</div>
						)}

						{isRelativeDates &&
							relativeDateStartDate &&
							formAuthors.length > 0 &&
							authorsDueDate != null && (
								<div
									key="relative-dates-banner"
									className="flex min-h-6 items-center gap-2 rounded border border-primary-3 bg-primary-1 px-3 py-2"
								>
									<Icon icon={faFlagPennant} size={16} color="primary-5" />
									<div className="flex flex-wrap items-center gap-x-1 text-paragraph-3 text-neutral-9">
										<span className="font-semibold">Authors Due:</span>
										<TextDate
											date={calculateRelativeDueDateInCompanyTz(
												relativeDateStartDate,
												authorsDueDate as number
											)}
											hasTime={true}
											dateSize="medium"
											compactTime={true}
										/>
										{formSigners.length > 0 && signersDueDate != null && (
											<>
												<span className="ml-1 font-semibold">Signers Due:</span>
												<TextDate
													date={calculateRelativeDueDateInCompanyTz(
														calculateRelativeDueDateInCompanyTz(
															relativeDateStartDate,
															authorsDueDate as number
														),
														signersDueDate as number
													)}
													hasTime={true}
													dateSize="medium"
													compactTime={true}
												/>
											</>
										)}
									</div>
								</div>
							)}

						<div className="flex flex-col gap-2">
							<div className="flex items-center gap-2">
								<Icon
									icon={makeVisibleWhenCompleted ? faCircleCheck : faCircleXmark}
									size={20}
									color={makeVisibleWhenCompleted ? "green-5" : "red-5"}
								/>
								<Text size="paragraph-3" color="neutral-9">
									Make visible to the subject when form is completed
								</Text>
							</div>
							<div className="flex items-center gap-2">
								<Icon
									icon={redactAuthors ? faCircleCheck : faCircleXmark}
									size={20}
									color={redactAuthors ? "green-5" : "red-5"}
								/>
								<Text size="paragraph-3" color="neutral-9">
									Redact authors
								</Text>
							</div>
						</div>
					</div>
				)}
			</FormSubscribe>
		);
	}

	// Editable/mixed view
	return (
		<div className={cn("grid grid-cols-[180px_1fr] items-start gap-4", dangerouslySetClassName)}>
			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor="formAuthors"
				dangerouslySetClassName="flex h-8 items-center"
			>
				Form Authors
			</Text>
			<div>
				{readOnly && !isFieldEditable("formAuthors") ? (
					<FormSubscribe
						id="standard-form-card-authors-readonly"
						selector={(state) => [state.values.formAuthors || []]}
					>
						{([formAuthors]) =>
							formAuthors.length > 0 ? (
								<SortableOrderedList<EmployeeValue>
									values={formAuthors}
									readOnly
									renderItem={renderEmployeeOrderedItemReadOnly}
									getItemId={(employee) => employee.id}
								/>
							) : (
								<Text size="paragraph-3" color="neutral-7">
									No authors selected
								</Text>
							)
						}
					</FormSubscribe>
				) : (
					<FormField<EmployeeValue[]>
						id="formAuthors"
						input={(field) => (
							<MultipleOrderedSelectEmployee
								value={field.value || []}
								onChange={field.onChange}
								placeholder="Search for authors..."
								disabled={!isFieldEditable("formAuthors")}
								staticOptions={STATIC_EMPLOYEE_OPTIONS}
								closeOnSelect={true}
							/>
						)}
					/>
				)}
			</div>

			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor="formSigners"
				dangerouslySetClassName="flex h-8 items-center"
			>
				Form Signers
			</Text>
			<div>
				{readOnly && !isFieldEditable("formSigners") ? (
					<FormSubscribe
						id="standard-form-card-signers-readonly"
						selector={(state) => [state.values.formSigners || []]}
					>
						{([formSigners]) =>
							formSigners.length > 0 ? (
								<SortableOrderedList<EmployeeValue>
									values={formSigners}
									readOnly
									renderItem={renderEmployeeOrderedItemReadOnly}
									getItemId={(employee) => employee.id}
								/>
							) : (
								<Text size="paragraph-3" color="neutral-7">
									No signers selected
								</Text>
							)
						}
					</FormSubscribe>
				) : (
					<FormField<EmployeeValue[]>
						id="formSigners"
						input={(field) => (
							<MultipleOrderedSelectEmployee
								value={field.value || []}
								onChange={field.onChange}
								placeholder="Search for signers..."
								disabled={!isFieldEditable("formSigners")}
								staticOptions={STATIC_EMPLOYEE_OPTIONS}
								closeOnSelect={true}
							/>
						)}
					/>
				)}
			</div>

			<FormSubscribe
				id="standard-form-card-authors-due-date-conditional"
				selector={(state) => [(state.values.formAuthors || []).length > 0]}
			>
				{([showAuthorsDueDate]) =>
					showAuthorsDueDate && (
						<React.Fragment key="authors-due-date">
							<Text
								size="paragraph-3"
								weight="semi-bold"
								color="neutral-9"
								overrideElement="label"
								htmlFor="authorsDueDate"
								dangerouslySetClassName="flex h-8 items-center"
							>
								Authors Due Date
							</Text>
							<div>
								{isRelativeDates ? (
									<FormField<number | null>
										id="authorsDueDate"
										input={() => (
											<div className="flex items-center gap-2">
												<div className="w-24">
													<FormInputNumeric
														id="authorsDueDate"
														disabled={!isFieldEditable("authorsDueDate")}
														allowEmpty={false}
														min={0}
													/>
												</div>
												<span
													className="text-[14px] leading-4 font-normal text-neutral-7"
													style={{ fontFamily: '"Source Sans Pro"' }}
												>
													Days from <span className="font-semibold">Start Date</span>
												</span>
											</div>
										)}
									/>
								) : (
									<FormDatePicker
										id="authorsDueDate"
										disabled={!isFieldEditable("authorsDueDate")}
										startDate={minimumDueDate}
										showTimeDropdown={true}
										defaultTime={defaultDueTime}
									/>
								)}
							</div>
						</React.Fragment>
					)
				}
			</FormSubscribe>

			<FormSubscribe
				id="standard-form-card-signers-due-date-conditional"
				selector={(state) => [(state.values.formSigners || []).length > 0]}
			>
				{([showSignersDueDate]) =>
					showSignersDueDate && (
						<React.Fragment key="signers-due-date">
							<Text
								size="paragraph-3"
								weight="semi-bold"
								color="neutral-9"
								overrideElement="label"
								htmlFor="signersDueDate"
								dangerouslySetClassName="flex h-8 items-center"
							>
								Signers Due Date
							</Text>
							<div>
								{isRelativeDates ? (
									<FormField<number | null>
										id="signersDueDate"
										input={() => (
											<div className="flex items-center gap-2">
												<div className="w-24">
													<FormInputNumeric
														id="signersDueDate"
														disabled={!isFieldEditable("signersDueDate")}
														allowEmpty={false}
														min={0}
													/>
												</div>
												<span
													className="text-[14px] leading-4 font-normal text-neutral-7"
													style={{ fontFamily: '"Source Sans Pro"' }}
												>
													Days from <span className="font-semibold">Authors Due Date</span>
												</span>
											</div>
										)}
									/>
								) : (
									<FormDatePicker
										id="signersDueDate"
										disabled={!isFieldEditable("signersDueDate")}
										startDate={minimumDueDate}
										showTimeDropdown={true}
										defaultTime={defaultDueTime}
									/>
								)}
							</div>
						</React.Fragment>
					)
				}
			</FormSubscribe>

			{isRelativeDates && relativeDateStartDate && (
				<FormSubscribe
					id="standard-form-card-relative-dates-banner-editable"
					selector={(state) => [
						(state.values.formAuthors || []).length > 0,
						(state.values.formSigners || []).length > 0,
						state.values.authorsDueDate,
						state.values.signersDueDate,
					]}
				>
					{([hasAuthors, hasSigners, authorsDueDate, signersDueDate]) =>
						hasAuthors &&
						authorsDueDate != null && (
							<div className="col-span-2 flex min-h-6 items-center gap-2 rounded border border-primary-3 bg-primary-1 px-3 py-2">
								<Icon icon={faFlagPennant} size={16} color="primary-5" />
								<div className="flex flex-wrap items-center gap-x-1 text-paragraph-3 text-neutral-9">
									<span className="font-semibold">Authors Due:</span>
									<TextDate
										date={calculateRelativeDueDateInCompanyTz(
											relativeDateStartDate,
											authorsDueDate as number
										)}
										hasTime={true}
										dateSize="medium"
										compactTime={true}
									/>
									{hasSigners && signersDueDate != null && (
										<>
											<span className="ml-1 font-semibold">Signers Due:</span>
											<TextDate
												date={calculateRelativeDueDateInCompanyTz(
													calculateRelativeDueDateInCompanyTz(
														relativeDateStartDate,
														authorsDueDate as number
													),
													signersDueDate as number
												)}
												hasTime={true}
												dateSize="medium"
												compactTime={true}
											/>
										</>
									)}
								</div>
							</div>
						)
					}
				</FormSubscribe>
			)}

			<div className="col-span-2 flex flex-col gap-2">
				<FormField<boolean>
					id="makeVisibleWhenCompleted"
					input={(field) => (
						<div className="flex items-center gap-2">
							<Checkbox
								id={`${formId}-makeVisibleWhenCompleted`}
								checked={field.value === true}
								onCheckedChange={(checked) => field.onChange(checked)}
								disabled={!isFieldEditable("makeVisibleWhenCompleted")}
							/>
							<Text
								size="paragraph-3"
								color="neutral-9"
								overrideElement="label"
								htmlFor={`${formId}-makeVisibleWhenCompleted`}
								dangerouslySetClassName="cursor-pointer"
							>
								Make visible to the subject when form is completed
							</Text>
						</div>
					)}
				/>
				<FormField<boolean>
					id="redactAuthors"
					input={(field) => (
						<div className="flex items-center gap-2">
							<Checkbox
								id={`${formId}-redactAuthors`}
								checked={field.value === true}
								onCheckedChange={(checked) => field.onChange(checked)}
								disabled={!isFieldEditable("redactAuthors")}
							/>
							<Text
								size="paragraph-3"
								color="neutral-9"
								overrideElement="label"
								htmlFor={`${formId}-redactAuthors`}
								dangerouslySetClassName="cursor-pointer"
							>
								Redact authors
							</Text>
						</div>
					)}
				/>
			</div>
		</div>
	);
};

StandardReviewFormCard.displayName = "StandardReviewFormCard";

/**
 * Combined props for StandardReviewForm wrapper component
 */
export type StandardReviewFormProps = Omit<ReviewFormContainerProps, "children"> &
	Omit<StandardReviewFormCardProps, "dangerouslySetClassName"> & {
		/**
		 * Optional CSS classes to apply to the outer container
		 */
		containerClassName?: string;
		/**
		 * Optional CSS classes to apply to the inner card
		 */
		cardClassName?: string;
	};

/**
 * StandardReviewForm combines ReviewFormContainer and StandardReviewFormCard
 * into a single component for easier usage.
 *
 * This is the recommended component to use. It includes the container with
 * header, delete button, and template icon functionality.
 */
export const StandardReviewForm = ({
	// Container props
	name,
	fromTemplate,
	onDelete,
	containerClassName,
	"data-testid": dataTestId,
	// Card props
	isRelativeDates,
	minimumDueDate,
	relativeDateStartDate,
	companyTimezone,
	defaultDueTime,
	readOnly,
	editableFields,
	cardClassName,
}: StandardReviewFormProps) => (
	<ReviewFormContainer
		name={name}
		fromTemplate={fromTemplate}
		onDelete={onDelete}
		dangerouslySetClassName={containerClassName}
		data-testid={dataTestId}
	>
		<StandardReviewFormCard
			isRelativeDates={isRelativeDates}
			minimumDueDate={minimumDueDate}
			relativeDateStartDate={relativeDateStartDate}
			companyTimezone={companyTimezone}
			defaultDueTime={defaultDueTime}
			readOnly={readOnly}
			editableFields={editableFields}
			dangerouslySetClassName={cardClassName}
		/>
	</ReviewFormContainer>
);

StandardReviewForm.displayName = "StandardReviewForm";
